import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import type React from "react";
import { useMemo, useState } from "react";

export const CUSTOMIZE_LIMIT_PER_APP = 10;

export interface CustomizeItem {
  /** カスタマイズ仕様書上の機能名（row[0]） */
  functionName: string;
  /** 適用アプリ名（row[1]） */
  appName: string;
}

export interface AppCustomizeSelection {
  appName: string;
  /** このアプリの全カスタマイズ件数（選択前） */
  totalCount: number;
  /** ユーザーが選択した機能名リスト */
  selectedFunctionNames: string[];
}

interface CustomizeSelectionDialogProps {
  /** ダイアログを開くか */
  open: boolean;
  /**
   * 10件超のアプリの全カスタマイズデータ。
   * appName でグループ化済みのデータを渡す。
   */
  appsExceedingLimit: {
    appName: string;
    items: CustomizeItem[];
  }[];
  onConfirm: (selections: AppCustomizeSelection[]) => void;
  onCancel: () => void;
}

/**
 * 1アプリあたりのカスタマイズ機能数が上限を超える場合に
 * ユーザーが適用する機能を選択するダイアログ。
 */
const CustomizeSelectionDialog: React.FC<CustomizeSelectionDialogProps> = ({
  open,
  appsExceedingLimit,
  onConfirm,
  onCancel,
}) => {
  // appName -> 選択済み機能名リスト
  const [selectionMap, setSelectionMap] = useState<Record<string, string[]>>(() =>
    Object.fromEntries(appsExceedingLimit.map((app) => [app.appName, []])),
  );

  const handleToggle = (appName: string, functionName: string) => {
    setSelectionMap((prev) => {
      const current = prev[appName] ?? [];
      if (current.includes(functionName)) {
        return { ...prev, [appName]: current.filter((n) => n !== functionName) };
      }
      if (current.length >= CUSTOMIZE_LIMIT_PER_APP) {
        return prev; // 上限超過は無視
      }
      return { ...prev, [appName]: [...current, functionName] };
    });
  };

  const handleSelectAll = (appName: string, items: CustomizeItem[]) => {
    const names = items.map((i) => i.functionName).slice(0, CUSTOMIZE_LIMIT_PER_APP);
    setSelectionMap((prev) => ({ ...prev, [appName]: names }));
  };

  const handleClearAll = (appName: string) => {
    setSelectionMap((prev) => ({ ...prev, [appName]: [] }));
  };

  // 全アプリで最低1件以上選択されているか
  const isAllAppsSelected = useMemo(
    () => appsExceedingLimit.every((app) => (selectionMap[app.appName]?.length ?? 0) > 0),
    [appsExceedingLimit, selectionMap],
  );

  const handleConfirm = () => {
    const selections: AppCustomizeSelection[] = appsExceedingLimit.map((app) => ({
      appName: app.appName,
      totalCount: app.items.length,
      selectedFunctionNames: selectionMap[app.appName] ?? [],
    }));
    onConfirm(selections);
  };

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, maxHeight: "85vh" } }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          カスタマイズ機能の選択
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          1アプリあたりの上限（{CUSTOMIZE_LIMIT_PER_APP}件）を超えるアプリがあります。
          適用するカスタマイズ機能を選択してください。
        </Typography>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ px: 3, py: 2 }}>
        <Alert
          severity="info"
          icon={<InfoOutlinedIcon fontSize="small" />}
          sx={{ mb: 2, borderRadius: 2, fontSize: "0.8rem" }}
        >
          10件以下のアプリはすべての機能が自動的に適用されます。 対象アプリのみ選択が必要です。
        </Alert>

        <Stack spacing={3}>
          {appsExceedingLimit.map((app) => {
            const selected = selectionMap[app.appName] ?? [];
            const selectedCount = selected.length;
            const progress = (selectedCount / CUSTOMIZE_LIMIT_PER_APP) * 100;

            return (
              <Box
                key={app.appName}
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  overflow: "hidden",
                }}
              >
                {/* アプリヘッダー */}
                <Box
                  sx={{
                    px: 2,
                    py: 1.5,
                    bgcolor: "grey.50",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Stack direction="row" alignItems="center" justifyContent="space-between" gap={1}>
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography variant="subtitle2" fontWeight={700}>
                        {app.appName}
                      </Typography>
                      <Chip
                        label={`計 ${app.items.length}件`}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ height: 20, fontSize: "0.7rem" }}
                      />
                    </Stack>
                    <Stack direction="row" gap={1}>
                      <Button
                        size="small"
                        variant="text"
                        onClick={() => handleSelectAll(app.appName, app.items)}
                        disabled={selectedCount >= CUSTOMIZE_LIMIT_PER_APP}
                        sx={{ fontSize: "0.7rem", minWidth: "auto", px: 1 }}
                      >
                        先頭{CUSTOMIZE_LIMIT_PER_APP}件を選択
                      </Button>
                      <Button
                        size="small"
                        variant="text"
                        color="inherit"
                        onClick={() => handleClearAll(app.appName)}
                        disabled={selectedCount === 0}
                        sx={{ fontSize: "0.7rem", minWidth: "auto", px: 1 }}
                      >
                        クリア
                      </Button>
                    </Stack>
                  </Stack>

                  {/* 選択件数プログレス */}
                  <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(progress, 100)}
                      color={selectedCount >= CUSTOMIZE_LIMIT_PER_APP ? "error" : "primary"}
                      sx={{ flex: 1, height: 6, borderRadius: 3 }}
                    />
                    <Typography
                      variant="caption"
                      color={
                        selectedCount === 0
                          ? "error"
                          : selectedCount >= CUSTOMIZE_LIMIT_PER_APP
                            ? "error.main"
                            : "text.secondary"
                      }
                      sx={{ whiteSpace: "nowrap", fontWeight: 600 }}
                    >
                      {selectedCount} / {CUSTOMIZE_LIMIT_PER_APP}件
                    </Typography>
                  </Stack>
                  {selectedCount === 0 && (
                    <Typography variant="caption" color="error" sx={{ display: "block", mt: 0.5 }}>
                      1件以上選択してください
                    </Typography>
                  )}
                </Box>

                {/* チェックボックスリスト */}
                <Box sx={{ px: 1.5, py: 1 }}>
                  <Stack spacing={0.25}>
                    {app.items.map((item) => {
                      const isChecked = selected.includes(item.functionName);
                      const isDisabled = !isChecked && selectedCount >= CUSTOMIZE_LIMIT_PER_APP;

                      return (
                        <FormControlLabel
                          key={item.functionName}
                          control={
                            <Checkbox
                              checked={isChecked}
                              disabled={isDisabled}
                              onChange={() => handleToggle(app.appName, item.functionName)}
                              size="small"
                              sx={{ py: 0.5 }}
                            />
                          }
                          label={
                            <Typography
                              variant="body2"
                              color={isDisabled ? "text.disabled" : "text.primary"}
                            >
                              {item.functionName}
                            </Typography>
                          }
                          sx={{ mx: 0, width: "100%" }}
                        />
                      );
                    })}
                  </Stack>
                </Box>
              </Box>
            );
          })}
        </Stack>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Button variant="text" color="inherit" onClick={onCancel}>
          キャンセル
        </Button>
        <Button
          variant="contained"
          onClick={handleConfirm}
          disabled={!isAllAppsSelected}
          sx={{ borderRadius: 2, px: 3 }}
        >
          この選択で実行
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomizeSelectionDialog;
